import { ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@shared/components/ui/card';
import { TabBadge } from '@shared/components/ui/tab-badge';
import { cn } from '@shared/lib/utils';
import { PANEL_SPACING } from '../../constants/spacing';
import { useSectionTitleClassName } from '../../context/SectionStyleContext';

interface SectionCardProps {
  /** Section title displayed at the top or left */
  title?: React.ReactNode;
  /** Optional item count rendered as a muted badge next to the title */
  count?: number;
  /** Main content of the section */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Layout mode: 'down' for stacked, 'detail' for detail view, 'two-column' for fixed left column */
  layout?: 'down' | 'detail' | 'two-column';
  /** Width of left column for two-column layout */
  leftColumnWidth?: string;
  /** Gap between title and content for detail layout */
  contentGap?: string;
  /** Whether the section can be collapsed */
  isCollapsible?: boolean;
  /** Whether the section is currently collapsed */
  isCollapsed?: boolean;
  /** Callback when title is clicked (for collapsible sections) */
  onTitleClick?: () => void;
  /** Actions to display in the header next to the title */
  headerActions?: React.ReactNode;
  /** Optional Tailwind height class inserted between the title and content (e.g. "h-16") */
  topSpacer?: string;
  /** Optional Tailwind width class to constrain content width (e.g. "w-2/3") */
  contentWidth?: string;
  /** When set, clicking anywhere in the card (except interactive elements) triggers this callback */
  onCardClick?: () => void;
}

export function SectionCard({
  title,
  count,
  children,
  className,
  layout = 'down',
  leftColumnWidth = 'w-48',
  contentGap = 'gap-16',
  isCollapsible = false,
  isCollapsed = false,
  onTitleClick,
  headerActions,
  topSpacer,
  contentWidth,
  onCardClick,
}: SectionCardProps) {
  const sectionTitleClassName = useSectionTitleClassName();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTitleClick?.();
    }
  };

  const titleElement = isCollapsible ? (
    <div className="flex items-center justify-between">
      <div 
        className={`${sectionTitleClassName} flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors`}
        onClick={onTitleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <span>{title}</span>
        {count != null && <TabBadge count={count} variant="muted" />}
        <ChevronDown 
          className={cn(
            "size-3 text-muted-foreground transition-transform duration-200",
            isCollapsed && "-rotate-90"
          )} 
        />
      </div>
      {headerActions}
    </div>
  ) : (
    <div className="flex items-center justify-between">
      <div className={cn(sectionTitleClassName, 'flex items-center')}>
        <span>{title}</span>
        {count != null && <TabBadge count={count} variant="muted" />}
      </div>
      {headerActions}
    </div>
  );

  const cardContent = (() => {
    switch (layout) {
      case 'detail':
        return (
          <div className="flex flex-col gap-3">
            {titleElement}
            {topSpacer && <div className={topSpacer} />}
            {!isCollapsed && (
              <div className={`flex items-start ${contentGap}`}>
                {children}
              </div>
            )}
          </div>
        );

      case 'two-column':
        return (
          <div className="flex">
            <div className={cn(leftColumnWidth, 'px-6 py-6 shrink-0')}>
              {titleElement}
            </div>
            <div className="flex-1 flex justify-center px-6 py-6">
              {topSpacer && <div className={topSpacer} />}
              {children}
            </div>
          </div>
        );

      default: // 'down'
        return (
          <div className={`flex flex-col ${PANEL_SPACING.SECTION_CONTENT_GAP}`}>
            {titleElement}
            {topSpacer && <div className={topSpacer} />}
            {!isCollapsed && (
              contentWidth
                ? <div className={contentWidth}>{children}</div>
                : children
            )}
          </div>
        );
    }
  })();

  return (
    <Card
      className={cn(PANEL_SPACING.SECTION_BOTTOM_MARGIN, onCardClick && 'cursor-pointer', className)}
      onClick={onCardClick}
    >
      <CardContent className={layout === 'two-column' ? 'p-0' : PANEL_SPACING.CARD_CONTENT_PADDING}>
        {cardContent}
      </CardContent>
    </Card>
  );
}
